import {
    allowedProhibitedOptions,
    expectedUsageOptions,
    offlineRetentionOptions,
    requiredOptions,
} from '@/modules/asset/config/asset';
import {
    AccrualPeriodicityInterval,
    AccrualPeriodicityUnits,
    AssetOfflineRetention,
    AssetTypeId,
} from '@/modules/asset/constants';
import { computed, Ref, watch } from '@vue/composition-api';
import * as R from 'ramda';
import { ExpectedUsage } from '../constants';
import { Contract, DerivationContract } from '../types';

export function useContractMetadata(
    contract: Ref<Contract | DerivationContract>,
    disabled: boolean,
    barteringAsset: Ref<{ id: number; name: string; storageType?: string; structureType?: string } | null>,
) {
    const isDerivationContract = computed(() => 'sellerOrgId' in contract);

    const accrualPeriodicityToSeconds = (accrual: { value: string | null; unit: string }) => {
        if (!accrual?.value) return null;
        const result = Number(accrual.value);
        switch (accrual.unit) {
            case AccrualPeriodicityInterval.Minute:
                return result * 60;
            case AccrualPeriodicityInterval.Hour:
                return result * 3600;
            case AccrualPeriodicityInterval.Day:
                return result * 86400;
            case AccrualPeriodicityInterval.Week:
                return result * 604800;
            case AccrualPeriodicityInterval.Month:
                return result * 2628000;
            default:
                return result;
        }
    };

    const assetAccrualPeriodicityOptions = computed(() => {
        const singular = contract.value.metadata.license.expectedUpdates?.value === '1';
        return Object.values(AccrualPeriodicityInterval).reduce(
            (acc: Record<string, string>, key: AccrualPeriodicityInterval) => {
                if (key !== AccrualPeriodicityInterval.NA)
                    acc[key] = singular
                        ? AccrualPeriodicityUnits[key].extendedSingular
                        : AccrualPeriodicityUnits[key].extendedPlural;
                else acc[key] = key;
                return acc;
            },
            {},
        );
    });

    const checkExpectedUsage = (expectedUsage: any) => {
        if (!expectedUsage) return;
        contract.value.metadata.license.expectedUsage = [expectedUsage.id];
        if (expectedUsage.id === ExpectedUsage.OnPremiseDataProvider)
            contract.value.metadata.license.offlineRetention = [];
    };

    const filteredExpectedUsageOptions = computed(() => {
        let options: any = R.clone(expectedUsageOptions);
        if (
            contract.value?.metadata?.license?.derivation === 'Prohibited' ||
            contract.value?.metadata?.license?.reproduction === 'Prohibited'
        )
            return options.filter((option: { id: string; label: string }) => option.id === ExpectedUsage.NotApplicable);
        options = options.filter((option: { id: string; label: string }) => option.id !== ExpectedUsage.NotApplicable);
        if (contract.value?.asset?.storage?.type === 'platform' || barteringAsset.value?.storageType === 'platform')
            return options.filter(
                (option: { id: string; label: string }) =>
                    option.id !== ExpectedUsage.OnPremiseDataProvider &&
                    (contract.value?.asset?.parquetStatus !== 'optimised' && contract.value?.asset?.assetTypeId !== 3
                        ? option.id !== ExpectedUsage.OnPremiseDataRecipient
                        : true),
            );
        return options;
    });

    const assetOfflineRetentionOptions = computed(() => {
        let options = R.clone(offlineRetentionOptions);
        // in case of "ExpectedUsage.OnPremiseDataProvider" do not allow offline retention
        if (
            contract.value?.asset?.assetTypeId === AssetTypeId.Model ||
            contract.value?.metadata?.license?.expectedUsage?.includes(ExpectedUsage.OnPremiseDataProvider)
        )
            return options.map((o: any) => ({ ...o, disabled: true, selectable: false }));
        // in case of view mode do not allow user to change offline retention
        if (disabled) options = options.map((o: any) => ({ ...o, disabled: true, selectable: true }));
        // in case of an other file do not allow API offline retention
        if (contract.value?.asset?.structure?.type === 'other' || barteringAsset.value?.structureType === 'other')
            options = options.map((o: any) =>
                o.value === AssetOfflineRetention.API ? { ...o, disabled: true, selectable: false } : o,
            );
        // in case of a data space storage do not allow File offline retention
        if (contract.value?.asset?.storage?.type === 'data-space' || barteringAsset.value?.storageType === 'data-space')
            options = options.map((o: any) =>
                o.value === AssetOfflineRetention.File ? { ...o, disabled: true, selectable: false } : o,
            );
        return options;
    });

    const getLicensingSchema = computed(() => {
        const schema: any = [];
        let items: any = [];
        if (contract.value?.asset?.assetTypeId !== AssetTypeId.Model)
            items = [
                {
                    type: 'radio',
                    name: 'derivation',
                    label: 'Derivation',
                    options: allowedProhibitedOptions,
                    help:
                        'An indication whether this contract shall permit the creation and distribution of any update, adaptation, or any other alteration of the asset, in terms of modification, excerpt, annotation, or aggregation.',
                    helpPosition: 'before',
                    validation: 'required',
                    errorBehavior: 'submit',
                    inputClass: 'inline-flex',
                    disabled: disabled || !!contract.value.updatingContractId,
                },
                {
                    type: 'radio',
                    name: 'attribution',
                    label: 'Attribution',
                    options: requiredOptions,
                    help:
                        'An indication whether this contract shall enforce giving credit to the copyright owner and/or asset provider.',
                    helpPosition: 'before',
                    validation: 'required',
                    errorBehavior: 'submit',
                    inputClass: 'inline-flex',
                    disabled: disabled || !!contract.value.updatingContractId,
                },
                {
                    type: 'radio',
                    name: 'reproduction',
                    label: 'Reproduction',
                    options: allowedProhibitedOptions,
                    help:
                        'An indication whether this contract shall allow temporary or permanent reproductions to be created by any means, in any form, in whole or in part from a given asset.',
                    helpPosition: 'before',
                    validation: 'required',
                    errorBehavior: 'submit',
                    inputClass: 'inline-flex',
                    disabled: disabled || !!contract.value.updatingContractId,
                },
                {
                    type: 'radio',
                    name: 'distribution',
                    label: 'Distribution',
                    options: allowedProhibitedOptions,
                    help:
                        'An indication whether this contract allows restricted or unrestricted publication and distribution of the asset or its derivatives.',
                    helpPosition: 'before',
                    validation: 'required',
                    errorBehavior: 'submit',
                    inputClass: 'inline-flex',
                    disabled: disabled || !!contract.value.updatingContractId,
                },
            ];
        items = items.concat([
            {
                type: 'treeselect',
                name: 'expectedUsage',
                label: 'Expected Usage',
                placeholder: 'Select expected usage',
                clearable: false,
                disableBranchNodes: true,
                options: filteredExpectedUsageOptions.value,
                help: isDerivationContract.value
                    ? 'The data derivative that utilizes the specific input data asset is allowed to become available in the following specified locations.'
                    : 'The specific data asset can be used for data analytics purposes in the following specified locations.',
                helpPosition: 'before',
                validation: 'required',
                validationName: 'expected Usage',
                labelClass: ['pb-1'],
                disabled:
                    disabled ||
                    contract.value.asset?.assetTypeId === AssetTypeId.Model ||
                    contract.value.metadata.license.expectedUsage?.includes(ExpectedUsage.NotApplicable) ||
                    !!contract.value.updatingContractId,
                input: () => {},
                select: checkExpectedUsage,
                errorBehavior: 'value',
            },
            {
                type: 'checkbox',
                name: 'offlineRetention',
                label: 'Offline Retention',
                options: assetOfflineRetentionOptions.value,
                help: isDerivationContract.value
                    ? 'An indication whether downloading the data derivative that utilizes the specific input data asset outside the Energy Data Space (e.g. as a file or through an API) is permitted.'
                    : 'An indication whether downloading outside the Energy Data Space (e.g. as a file or through an API) is permitted for the data asset.',
                helpPosition: 'before',
                inputClass: 'inline-flex',
            },
        ]);
        Array.prototype.push.apply(schema, items);
        return schema;
    });

    const getExpectedUpdatesSchema = computed(() => {
        const schema: any = [];
        const items: any = [];
        const asset = isDerivationContract.value
            ? (contract.value as DerivationContract).sellingAsset
            : contract.value.asset;
        if (
            contract.value.metadata.license.expectedUpdates?.unit &&
            contract.value.metadata.license.expectedUpdates.unit !== AccrualPeriodicityInterval.NA
        ) {
            const { unit: assetUnit, value: assetValue } = asset?.metadata?.distribution?.accrualPeriodicity;
            const contractSeconds = accrualPeriodicityToSeconds(contract.value.metadata.license.expectedUpdates);
            const assetSeconds = accrualPeriodicityToSeconds(asset?.metadata?.distribution?.accrualPeriodicity);
            items.push({
                type: 'number',
                name: 'value',
                key: 'expectedUpdatesValue',
                placeholder: 'Enter expected updates',
                helpPosition: 'before',
                validation: `bail|required|greaterThanAsset|lessThanOrEqual:1000`,
                validationRules: {
                    greaterThanAsset: () => (contractSeconds && assetSeconds ? contractSeconds >= assetSeconds : false),
                    lessThanOrEqual: ({ value }: any) => value <= 1000,
                },
                validationMessages: {
                    greaterThanAsset: `Expected updates must be greater or equal to ${assetValue} ${assetUnit}.`,
                    lessThanOrEqual: 'Expected updates must be less than or equal to 1000.',
                },
                validationName: 'expected Updates',
                inputClass: 'form-input',
                errorBehavior: 'value',
                class: 'w-full',
                disabled,
            });
        }
        items.push({
            type: 'select',
            name: 'unit',
            key: 'expectedUpdatesUnit',
            placeholder: 'Select expected updates unit',
            options: assetAccrualPeriodicityOptions.value,
            helpPosition: 'before',
            validation: 'required',
            disabled:
                disabled || asset?.metadata.distribution.accrualPeriodicity?.unit === AccrualPeriodicityInterval.NA,
            validationName: 'expected Updates',
            inputClass: 'form-select',
            errorBehavior: 'value',
            class: 'w-full',
        });
        Array.prototype.push.apply(schema, items);
        return schema;
    });

    watch(
        () => contract.value?.metadata?.license?.derivation,
        (newDerivation) => {
            if (newDerivation === 'Prohibited')
                contract.value.metadata.license.expectedUsage = [ExpectedUsage.NotApplicable];
            else if (
                contract.value.metadata.license.reproduction !== 'Prohibited' &&
                contract.value.metadata.license.expectedUsage?.includes(ExpectedUsage.NotApplicable)
            )
                contract.value.metadata.license.expectedUsage = null;
        },
    );

    watch(
        () => contract.value?.metadata?.license?.reproduction,
        (newReproduction) => {
            if (newReproduction === 'Prohibited')
                contract.value.metadata.license.expectedUsage = [ExpectedUsage.NotApplicable];
            else if (
                contract.value.metadata.license.derivation !== 'Prohibited' &&
                contract.value.metadata.license.expectedUsage?.includes(ExpectedUsage.NotApplicable)
            )
                contract.value.metadata.license.expectedUsage = null;
        },
    );

    return { getLicensingSchema, getExpectedUpdatesSchema };
}
